'use client';

import React from 'react';

const testimonials = [
  {
    quote: 'Our cruise exceeded every expectation. The service was exceptional, the destinations breathtaking, and the exclusive members only discount saved us 1000s. We\'re already planning our next trip.',
    author: 'Margaret & David H.',
    location: 'Sydney, Australia',
    cruise: 'Mediterranean, 2025',
    initials: 'MH',
  },
  {
    quote: 'Joining Ultra Luxury Cruise Club was the best travel decision we\'ve ever made. The exclusive access to world-class ships, personalised service, and member savings transformed our Norwegian Fjords voyage into something truly unforgettable.',
    author: 'James & Claire T.',
    location: 'Melbourne, Australia',
    cruise: 'Norwegian Fjords, 2025',
    initials: 'JT',
  },
  {
    quote: 'This isn\'t just a cruise — it\'s a celebration of life. Every moment was filled with joy and indulgence. The membership pays for itself on the very first booking.',
    author: 'Robert & Susan M.',
    location: 'Brisbane, Australia',
    cruise: 'South Pacific, 2025',
    initials: 'RM',
  },
];

export default function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-28 bg-cream-light relative overflow-hidden">
      {/* Decorative element */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-gold/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-10 bg-gold" />
            <span className="text-gold text-xs tracking-[0.3em] uppercase font-light">Member Stories</span>
            <div className="h-px w-10 bg-gold" />
          </div>
          <h2 className="font-serif text-navy-dark text-4xl md:text-5xl font-light">
            What Our Members{' '}
            <span className="italic text-primary">Say</span>
          </h2>
        </div>

        {/* Testimonials — asymmetric layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Large featured testimonial */}
          <div className="md:col-span-6 bg-navy-dark rounded-sm p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-gold/5 rounded-full translate-x-1/2 -translate-y-1/2 blur-2xl" />
            {/* Quote mark */}
            <div className="font-serif text-gold/20 text-9xl leading-none absolute top-4 left-6 select-none">"</div>
            <div className="relative z-10">
              <p className="text-white/80 text-lg font-light leading-relaxed mb-8 mt-6">
                {testimonials?.[0]?.quote}
              </p>
              <div className="flex items-center gap-4 border-t border-white/10 pt-6">
                <div className="w-12 h-12 rounded-full bg-gold-gradient flex items-center justify-center text-navy-dark font-medium text-sm">
                  {testimonials?.[0]?.initials}
                </div>
                <div>
                  <div className="text-white font-medium text-sm">{testimonials?.[0]?.author}</div>
                  <div className="text-white/40 text-xs mt-0.5">{testimonials?.[0]?.location}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-gold/60 text-xs tracking-[0.1em] uppercase">{testimonials?.[0]?.cruise}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Two smaller testimonials */}
          <div className="md:col-span-6 flex flex-col gap-6">
            {testimonials?.slice(1)?.map((t) => (
              <div key={t?.author} className="bg-white border border-cream-dark rounded-sm p-8 relative overflow-hidden flex-1">
                <div className="font-serif text-navy-dark/10 text-7xl leading-none absolute top-2 left-4 select-none">"</div>
                <div className="relative z-10">
                  <p className="text-navy-dark/70 font-light leading-relaxed mb-6 mt-2">
                    {t?.quote}
                  </p>
                  <div className="flex items-center gap-3 border-t border-cream-dark pt-4">
                    <div className="w-10 h-10 rounded-full bg-navy-dark flex items-center justify-center text-white text-xs font-medium">
                      {t?.initials}
                    </div>
                    <div>
                      <div className="text-navy-dark font-medium text-sm">{t?.author}</div>
                      <div className="text-navy-dark/40 text-xs">{t?.location}</div>
                    </div>
                    <div className="ml-auto">
                      <div className="text-gold text-xs tracking-[0.1em] uppercase">{t?.cruise}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Star rating bar */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 py-6 border-t border-cream-dark">
          <div className="flex items-center gap-1">
            {[...Array(5)]?.map((_, i) => (
              <svg key={i} className="w-5 h-5 text-gold" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <p className="text-navy-dark/50 text-sm font-light">
            Rated <span className="text-navy-dark font-medium">5.0</span> by our members — consistently exceptional experiences
          </p>
        </div>
      </div>
    </section>
  );
}
